import React from "react";
import { InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function normalizeFraction(numerator, denominator) {
  if (denominator < 0) return normalizeFraction(-numerator, -denominator);
  if (numerator === 0) return { numerator: 0, denominator: 1 };
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function randomProperFraction(denominator = randInt(3, 10)) {
  const candidates = Array.from({ length: denominator - 1 }, (_, index) => index + 1).filter(
    (numerator) => gcd(numerator, denominator) === 1
  );
  return { numerator: pick(candidates), denominator };
}

function reciprocal(fraction) {
  return normalizeFraction(fraction.denominator, fraction.numerator);
}

function multiplyFractions(a, b) {
  return normalizeFraction(a.numerator * b.numerator, a.denominator * b.denominator);
}

function divideFractions(a, b) {
  return multiplyFractions(a, reciprocal(b));
}

function addFractions(a, b) {
  return normalizeFraction(a.numerator * b.denominator + b.numerator * a.denominator, a.denominator * b.denominator);
}

function subtractFractions(a, b) {
  return normalizeFraction(a.numerator * b.denominator - b.numerator * a.denominator, a.denominator * b.denominator);
}

function fractionLatex(numerator, denominator) {
  if (denominator === 1) return `${numerator}`;
  if (numerator < 0) return `-\\frac{${Math.abs(numerator)}}{${denominator}}`;
  return `\\frac{${numerator}}{${denominator}}`;
}

function fractionValueLatex(fraction) {
  return fractionLatex(fraction.numerator, fraction.denominator);
}

function mixedLatex({ whole, numerator, denominator }) {
  if (!numerator) return `${whole}`;
  const sign = whole < 0 ? "-" : "";
  return `${sign}${Math.abs(whole)}\\frac{${numerator}}{${denominator}}`;
}

function fractionToMixed(fraction) {
  const sign = fraction.numerator < 0 ? -1 : 1;
  const absNumerator = Math.abs(fraction.numerator);
  const whole = Math.floor(absNumerator / fraction.denominator) * sign;
  const rest = absNumerator % fraction.denominator;
  const simpleRest = normalizeFraction(rest, fraction.denominator);
  return { whole, numerator: simpleRest.numerator, denominator: simpleRest.denominator };
}

function displayFractionLatex(fraction) {
  return fraction.display === "mixed" ? mixedLatex(fractionToMixed(fraction)) : fractionValueLatex(fraction);
}

function answerLatex(fraction) {
  if (Math.abs(fraction.numerator) > fraction.denominator && fraction.denominator !== 1) {
    return mixedLatex(fractionToMixed(fraction));
  }
  return fractionValueLatex(fraction);
}

function MathText({ math, size = "1.35rem" }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.15 }}>
      <InlineMath math={math} />
    </span>
  );
}

function makeMixedFraction(whole = randInt(1, 5), denominator = randInt(3, 9)) {
  const rest = randomProperFraction(denominator);
  return {
    numerator: whole * denominator + rest.numerator,
    denominator,
    display: "mixed",
  };
}

function expressionLatex(terms, useDisplay = true) {
  return terms
    .map((term, index) => {
      const body = useDisplay ? displayFractionLatex(term.fraction) : fractionValueLatex(term.fraction);
      if (index === 0) return body;
      if (term.op === ":") return `:${body}`;
      return `${term.op}${body}`;
    })
    .join("");
}

function parseRawFraction(raw) {
  if (typeof raw !== "string") return null;
  const normalized = raw.trim().replace(",", ".").replace(/[−–]/g, "-");
  const mixed = normalized.replace(/\s+/g, " ").match(/^(-?\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const numerator = Number(mixed[2]);
    const denominator = Number(mixed[3]);
    if (!Number.isInteger(whole) || !Number.isInteger(numerator) || !Number.isInteger(denominator)) return null;
    if (denominator === 0 || numerator <= 0 || numerator >= denominator) return null;
    const sign = whole < 0 ? -1 : 1;
    return normalizeFraction(sign * (Math.abs(whole) * denominator + numerator), denominator);
  }
  const cleaned = normalized.replace(/\s+/g, "");
  const match = cleaned.match(/^(-?\d+)\/(-?\d+)$/);
  if (match) {
    const numerator = Number(match[1]);
    const denominator = Number(match[2]);
    if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator === 0) return null;
    return normalizeFraction(numerator, denominator);
  }
  if (/^-?\d+$/.test(cleaned)) return { numerator: Number(cleaned), denominator: 1 };
  return null;
}

function evaluateTerms(terms) {
  let current = terms[0].fraction;
  let result = null;
  let pendingSign = "+";

  for (let i = 1; i < terms.length; i += 1) {
    const term = terms[i];
    if (term.op === ":") {
      current = divideFractions(current, term.fraction);
    } else {
      result = result
        ? pendingSign === "+"
          ? addFractions(result, current)
          : subtractFractions(result, current)
        : pendingSign === "+"
        ? current
        : normalizeFraction(-current.numerator, current.denominator);
      pendingSign = term.op;
      current = term.fraction;
    }
  }

  return result
    ? pendingSign === "+"
      ? addFractions(result, current)
      : subtractFractions(result, current)
    : current;
}

function makeTask(type, terms) {
  return { type, data: { terms, result: evaluateTerms(terms) } };
}

function simpleDivisionTask() {
  return makeTask("FractionDivideSimpleTask", [
    { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
    { op: ":", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function cancellableDivisionTask() {
  const pairs = [
    [{ numerator: 2, denominator: 9 }, { numerator: 4, denominator: 15 }],
    [{ numerator: 5, denominator: 12 }, { numerator: 10, denominator: 21 }],
    [{ numerator: 3, denominator: 8 }, { numerator: 9, denominator: 14 }],
    [{ numerator: 7, denominator: 15 }, { numerator: 14, denominator: 25 }],
  ];
  const [left, right] = pick(pairs);
  return makeTask("FractionDivideCancellableTask", [
    { op: "+", fraction: left },
    { op: ":", fraction: right },
  ]);
}

function improperDivisionTask() {
  const d1 = randInt(3, 8);
  const left = normalizeFraction(randInt(d1 + 1, d1 * 3 - 1), d1);
  return makeTask("FractionDivideImproperTask", [
    { op: "+", fraction: left },
    { op: ":", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function divisionPlusTask() {
  return makeTask("FractionDividePlusTask", [
    { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
    { op: ":", fraction: randomProperFraction(randInt(3, 9)) },
    { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function divisionMinusTask() {
  let terms;
  do {
    terms = [
      { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "-", fraction: randomProperFraction(randInt(3, 9)) },
      { op: ":", fraction: randomProperFraction(randInt(3, 9)) },
    ];
  } while (evaluateTerms(terms).numerator <= 0);
  return makeTask("FractionDivideMinusTask", terms);
}

function mixedDivisionTask() {
  return makeTask("FractionDivideMixedTask", [
    { op: "+", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 8)) },
    { op: ":", fraction: randomProperFraction(randInt(3, 9)) },
  ]);
}

function mixedWithOperationTask() {
  const operation = pick(["+", "-"]);
  if (operation === "+") {
    return makeTask("FractionDivideMixedOperationTask", [
      { op: "+", fraction: makeMixedFraction(randInt(1, 3), randInt(3, 8)) },
      { op: ":", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
    ]);
  }

  let terms;
  do {
    terms = [
      { op: "+", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 8)) },
      { op: ":", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "-", fraction: randomProperFraction(randInt(3, 9)) },
    ];
  } while (evaluateTerms(terms).numerator <= 0);
  return makeTask("FractionDivideMixedOperationTask", terms);
}

export function generateTasks() {
  return [
    simpleDivisionTask(),
    cancellableDivisionTask(),
    improperDivisionTask(),
    divisionPlusTask(),
    divisionMinusTask(),
    mixedDivisionTask(),
    mixedWithOperationTask(),
  ];
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <MathText math={expressionLatex(data.terms)} />.
    </p>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label="wynik" placeholder="np. 3/8" />;
}

export function checkAnswer({ data }, userAnswer) {
  const raw = parseRawFraction(userAnswer?.raw);
  if (!raw) return false;
  return raw.numerator === data.result.numerator && raw.denominator === data.result.denominator;
}

export function renderAnswer({ data }) {
  return <MathText math={answerLatex(data.result)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  const parsed = parseRawFraction(userAnswer.raw);
  return <MathText math={parsed ? answerLatex(parsed) : userAnswer.raw} />;
}

function mixedConversionLine(term) {
  if (term.fraction.display !== "mixed") return null;
  const mixed = fractionToMixed(term.fraction);
  return `${displayFractionLatex(term.fraction)}=\\frac{${mixed.whole}\\cdot ${mixed.denominator}+${mixed.numerator}}{${mixed.denominator}}=${fractionValueLatex(
    term.fraction
  )}`;
}

function divisionLines(terms) {
  const groups = [];
  let current = [terms[0]];
  for (let i = 1; i < terms.length; i += 1) {
    if (terms[i].op === ":") current.push(terms[i]);
    else {
      groups.push(current);
      current = [terms[i]];
    }
  }
  groups.push(current);

  return groups
    .filter((group) => group.length > 1)
    .map((group) => {
      const divisor = group[1].fraction;
      const reciprocalDivisor = reciprocal(divisor);
      const product = divideFractions(group[0].fraction, divisor);
      return `${fractionValueLatex(group[0].fraction)}:${fractionValueLatex(divisor)}=${fractionValueLatex(
        group[0].fraction
      )}\\cdot ${fractionValueLatex(reciprocalDivisor)}=${fractionLatex(
        `${group[0].fraction.numerator}\\cdot ${reciprocalDivisor.numerator}`,
        `${group[0].fraction.denominator}\\cdot ${reciprocalDivisor.denominator}`
      )}=${fractionValueLatex(product)}`;
    });
}

function productExpressionAfterDivision(terms) {
  return terms
    .map((term, index) => {
      const body = fractionValueLatex(term.fraction);
      if (index === 0) return body;
      if (term.op === ":") return `\\cdot ${fractionValueLatex(reciprocal(term.fraction))}`;
      return `${term.op}${body}`;
    })
    .join("");
}

export function renderExplanation({ type, data }) {
  const mixedConversions = data.terms.map(mixedConversionLine).filter(Boolean);
  const lines = divisionLines(data.terms);
  return (
    <div>
      {mixedConversions.length > 0 && (
        <>
          <p>Najpierw zamieniamy liczby mieszane na ułamki niewłaściwe.</p>
          {mixedConversions.map((line, index) => (
            <p key={`mixed-${index}`}>
              <MathText math={line} />
            </p>
          ))}
        </>
      )}
      <p>Dzielenie przez ułamek zamieniamy na mnożenie przez jego odwrotność.</p>
      {lines.map((line, index) => (
        <p key={`line-${index}`}>
          <MathText math={line} />
        </p>
      ))}
      {type === "FractionDivideCancellableTask" && (
        <p>Po zamianie dzielenia na mnożenie można skrócić na krzyż, żeby rachunek był prostszy.</p>
      )}
      <p>
        <MathText math={`${expressionLatex(data.terms, false)}=${productExpressionAfterDivision(data.terms)}=${fractionValueLatex(data.result)}`} />
      </p>
      {Math.abs(data.result.numerator) > data.result.denominator && data.result.denominator !== 1 && (
        <p>
          Ułamek niewłaściwy zapisujemy jako liczbę mieszaną:{" "}
          <MathText math={`${fractionValueLatex(data.result)}=${mixedLatex(fractionToMixed(data.result))}`} />.
        </p>
      )}
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  FractionDivideSimpleTask: taskAdapter,
  FractionDivideCancellableTask: taskAdapter,
  FractionDivideImproperTask: taskAdapter,
  FractionDividePlusTask: taskAdapter,
  FractionDivideMinusTask: taskAdapter,
  FractionDivideMixedTask: taskAdapter,
  FractionDivideMixedOperationTask: taskAdapter,
};
